import { useState, useEffect, useCallback } from 'react';
import { useWorkspace } from '../context/WorkspaceContext';
import { apiFetch } from '../fetchInterceptor';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { HelpPanel } from '../components/ui/help-panel';
import { invoicesHelp } from '../lib/helpContent';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Skeleton } from '../components/ui/skeleton';
import { 
  FileText, Plus, Search, Send, DollarSign, Trash2, Loader2,
  CheckCircle2, XCircle, Calendar, User, ExternalLink, Upload,
  ChevronDown, FileUp, Pencil, Eye, MoreHorizontal, Clock, Download,
  TrendingUp, AlertTriangle, ArrowUpRight, ArrowDownRight, Filter,
  ScanLine, Repeat, Play, Pause, ArrowUpDown, ArrowUp, ArrowDown, Info,
  RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { downloadPdfBlob } from '../lib/downloadPdfBlob';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '../components/ui/tooltip';
import RecentOCRUploads from '../components/RecentOCRUploads';
import DocumentScanner from '../components/DocumentScanner';
import MobileQuickActions from '../components/layout/MobileQuickActions';
import PaymentsSetupBanner from '../components/dashboard/PaymentsSetupBanner';
import { SavedViews } from '../components/SavedViews';
import { SearchableContactSelect } from '../components/ui/searchable-contact-select';
import { SearchableAccountSelect } from '../components/ui/searchable-account-select';
import { SearchableDescriptionSelect } from '../components/ui/searchable-description-select';
import { CurrencyInput } from '../components/ui/currency-input';
import { InvoicePreview } from '../components/invoice';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD', minimumFractionDigits: 2,
  }).format(amount || 0);
};

const formatCompact = (amount) => {
  if (Math.abs(amount) >= 1000) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency', currency: 'USD',
      notation: 'compact', maximumFractionDigits: 1,
    }).format(amount || 0);
  }
  return formatCurrency(amount);
};

const StatusBadge = ({ status }) => {
  const styles = {
    draft: 'bg-gray-100 text-gray-700 border-gray-300 dark:bg-gray-700/50 dark:text-gray-200 dark:border-gray-600',
    sent: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/40 dark:text-blue-300 dark:border-blue-700',
    partial: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/40 dark:text-amber-300 dark:border-amber-700',
    paid: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-300 dark:border-emerald-700',
    overdue: 'bg-rose-50 text-rose-700 border-rose-200 dark:bg-rose-900/40 dark:text-rose-300 dark:border-rose-700',
    voided: 'bg-zinc-100 text-zinc-500 border-zinc-200 dark:bg-zinc-800 dark:text-zinc-400 dark:border-zinc-700',
  };
  
  const icons = {
    draft: <FileText className="w-3 h-3" />,
    sent: <Send className="w-3 h-3" />,
    partial: <Clock className="w-3 h-3" />,
    paid: <CheckCircle2 className="w-3 h-3" />,
    overdue: <AlertTriangle className="w-3 h-3" />,
    voided: <XCircle className="w-3 h-3" />,
  };
  
  return (
    <Badge 
      variant="outline" 
      className={cn('text-[11px] font-medium capitalize rounded-full px-2.5 py-0.5 gap-1 border', styles[status])}
      data-testid={`status-badge-${status}`}
    >
      {icons[status]}
      {status}
    </Badge>
  );
};

// Metric Card
const MetricCard = ({ label, value, icon: Icon, trend, trendLabel, variant = 'default', tooltip }) => {
  const variantStyles = {
    default: 'border-border',
    warning: 'border-amber-200 dark:border-amber-900/50',
    danger: 'border-rose-200 dark:border-rose-900/50',
    success: 'border-emerald-200 dark:border-emerald-900/50',
  };
  
  return (
    <div className={cn(
      "flex flex-col justify-between p-5 rounded-xl border bg-card/50 transition-shadow duration-200 hover:shadow-md",
      variantStyles[variant]
    )} data-testid={`metric-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-1.5">
          <span className="text-xs tracking-[0.15em] uppercase font-semibold text-muted-foreground">{label}</span>
          {tooltip && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="w-3 h-3 text-muted-foreground/50 cursor-help" />
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-[220px]">
                  <p className="text-xs">{tooltip}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
        <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-muted">
          <Icon className="w-4 h-4 text-muted-foreground" />
        </div>
      </div>
      <p className="text-2xl font-semibold tracking-tight text-foreground" style={{ fontFamily: "'Outfit', sans-serif" }}>
        {value}
      </p>
      {(trend !== undefined || trendLabel) && (
        <div className="flex items-center gap-1.5 mt-2">
          {trend !== undefined && (
            <span className={cn(
              "flex items-center gap-0.5 text-xs font-medium",
              trend >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-rose-600 dark:text-rose-400"
            )}>
              {trend >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
            </span>
          )}
          <span className="text-xs text-muted-foreground">{trendLabel}</span>
        </div>
      )}
    </div>
  );
};

// Aging Bar
const AgingBar = ({ aging, total }) => {
  const buckets = [
    { key: 'current', label: 'Current', color: 'bg-emerald-500', textColor: 'text-emerald-700 dark:text-emerald-400' },
    { key: 'days_1_30', label: '1-30d', color: 'bg-blue-500', textColor: 'text-blue-700 dark:text-blue-400' },
    { key: 'days_31_60', label: '31-60d', color: 'bg-amber-500', textColor: 'text-amber-700 dark:text-amber-400' },
    { key: 'days_61_90', label: '61-90d', color: 'bg-orange-500', textColor: 'text-orange-700 dark:text-orange-400' },
    { key: 'days_90_plus', label: '90+d', color: 'bg-rose-500', textColor: 'text-rose-700 dark:text-rose-400' },
  ];

  if (!total || total <= 0) return null;

  return (
    <div className="space-y-3" data-testid="aging-breakdown">
      <div className="flex h-2 rounded-full overflow-hidden bg-muted">
        {buckets.map(b => {
          const val = aging[b.key] || 0;
          const pct = (val / total) * 100;
          if (pct <= 0) return null;
          return (
            <div key={b.key} className={cn("h-full transition-all duration-500", b.color)} style={{ width: `${pct}%` }} />
          );
        })}
      </div>
      <div className="flex flex-wrap gap-x-5 gap-y-1">
        {buckets.map(b => {
          const val = aging[b.key] || 0;
          if (val <= 0) return null;
          return (
            <div key={b.key} className="flex items-center gap-2">
              <div className={cn("w-2 h-2 rounded-full", b.color)} />
              <span className="text-xs text-muted-foreground">{b.label}</span>
              <span className={cn("text-xs font-semibold", b.textColor)}>{formatCompact(val)}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};


// Main Invoices Page
export default function Invoices() {
  const { currentWorkspace } = useWorkspace();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [posting, setPosting] = useState(null);
  const [arMetrics, setArMetrics] = useState({});
  const [arAging, setArAging] = useState({});
  const [arInsight, setArInsight] = useState(null);
  
  // Invoice Preview State
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewInvoice, setPreviewInvoice] = useState(null);
  // Row currently selected for mobile quick-actions bottom sheet (lg:hidden)
  const [mobileActionsInv, setMobileActionsInv] = useState(null);
  const [invoiceTheme, setInvoiceTheme] = useState('blue');
  const [invoiceLogo, setInvoiceLogo] = useState(null);
  const [customColor, setCustomColor] = useState(null);
  const [defaultTaxRate, setDefaultTaxRate] = useState(0);
  const [companyInfo, setCompanyInfo] = useState({ name: '', address: '', email: '', phone: '', website: '', tagline: '' });
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [invoiceScannerOpen, setInvoiceScannerOpen] = useState(false);
  
  // Recurring Invoices
  const [viewTab, setViewTab] = useState('invoices');
  const [recurringInvoices, setRecurringInvoices] = useState([]);
  const [recurringLoading, setRecurringLoading] = useState(false);
  const [recurringFormOpen, setRecurringFormOpen] = useState(false);
  const [recurringSaving, setRecurringSaving] = useState(false);
  const [generatingRec, setGeneratingRec] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [recForm, setRecForm] = useState({
    name: '', customer_id: '', frequency: 'monthly', start_date: '', end_date: '',
    auto_send: false, memo: '', payment_terms: 30,
    line_items: [{ description: '', quantity: 1, unit_price: 0, account_id: '', tax_rate: 0 }],
  });
  
  useEffect(() => {
    if (searchParams.get('new') === 'true') {
      // Legacy ?new=true links → redirect to the new full-page form
      navigate('/invoices/new', { replace: true });
    }
  }, [searchParams, navigate]);

  // Auto-open the preview modal when `?preview=<invoice_id>` is set —
  // used by deep-links from the dashboard (Cash Flow Forecast widget,
  // notifications, etc.) so clicking an invoice card from anywhere
  // lands here with the right invoice already open.
  useEffect(() => {
    const previewId = searchParams.get('preview');
    if (!previewId || loading) return;
    const match = invoices.find((inv) => inv.invoice_id === previewId);
    if (match) {
      handlePreviewInvoice(match);
      // Strip the param so a manual refresh doesn't re-open the modal.
      const next = new URLSearchParams(searchParams);
      next.delete('preview');
      setSearchParams(next, { replace: true });
    }
    // We intentionally don't depend on handlePreviewInvoice (stable enough)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, invoices, loading]);
  
  const handleEditInvoice = (invoice) => {
    navigate(`/invoices/${invoice.invoice_id}/edit`);
  };
  
  const handlePreviewInvoice = async (invoice) => {
    try {
      if (currentWorkspace) {
        try {
          const settingsResponse = await apiFetch(`${API_URL}/api/workspaces/${currentWorkspace.workspace_id}/invoice-settings`);
          if (settingsResponse.ok) {
            const settings = await settingsResponse.json();
            setInvoiceTheme(settings.theme || 'blue');
            setInvoiceLogo(settings.logo || null);
            setCustomColor(settings.customColor || null);
            setDefaultTaxRate(settings.defaultTaxRate || 0);
            setCompanyInfo(settings.companyInfo || { name: currentWorkspace.name || '' });
          }
        } catch {
          const savedSettings = localStorage.getItem(`invoice_settings_${currentWorkspace.workspace_id}`);
          if (savedSettings) {
            const s = JSON.parse(savedSettings);
            setInvoiceTheme(s.theme || 'blue');
            setInvoiceLogo(s.logo || null);
            setCustomColor(s.customColor || null);
            setDefaultTaxRate(s.defaultTaxRate || 0);
            setCompanyInfo(s.companyInfo || { name: currentWorkspace.name || '' });
          }
        }
      }
      
      const response = await apiFetch(`${API_URL}/api/invoices/${invoice.invoice_id}`);
      if (response.ok) {
        const fullInvoice = await response.json();
        setPreviewInvoice(fullInvoice);
        setPreviewOpen(true);
      } else { toast.error('Failed to load invoice details'); }
    } catch { toast.error('Failed to load invoice details'); }
  };
  
  // Load invoice settings
  useEffect(() => {
    const fetchInvoiceSettings = async () => {
      if (!currentWorkspace) return;
      try {
        const response = await apiFetch(`${API_URL}/api/workspaces/${currentWorkspace.workspace_id}/invoice-settings`);
        if (response.ok) {
          const settings = await response.json();
          setInvoiceTheme(settings.theme || 'blue');
          setInvoiceLogo(settings.logo || null);
          setCustomColor(settings.customColor || null);
          setDefaultTaxRate(settings.defaultTaxRate || 0);
          setCompanyInfo(settings.companyInfo || { name: currentWorkspace.name || '' });
        } else {
          const savedSettings = localStorage.getItem(`invoice_settings_${currentWorkspace.workspace_id}`);
          if (savedSettings) {
            const s = JSON.parse(savedSettings);
            setInvoiceTheme(s.theme || 'blue'); setInvoiceLogo(s.logo || null); setCustomColor(s.customColor || null);
            setDefaultTaxRate(s.defaultTaxRate || 0);
            setCompanyInfo(s.companyInfo || { name: currentWorkspace.name || '' });
          } else {
            setCompanyInfo(prev => ({ ...prev, name: currentWorkspace.name || '' }));
          }
        }
      } catch {
        const savedSettings = localStorage.getItem(`invoice_settings_${currentWorkspace.workspace_id}`);
        if (savedSettings) {
          const s = JSON.parse(savedSettings);
          setInvoiceTheme(s.theme || 'blue'); setInvoiceLogo(s.logo || null); setCustomColor(s.customColor || null);
          setDefaultTaxRate(s.defaultTaxRate || 0);
          setCompanyInfo(s.companyInfo || { name: currentWorkspace.name || '' });
        }
      } finally { setSettingsLoaded(true); }
    };
    fetchInvoiceSettings();
  }, [currentWorkspace]);
  
  // Auto-save settings
  const saveInvoiceSettings = useCallback(async () => {
    if (!currentWorkspace) return;
    try {
      await apiFetch(`${API_URL}/api/workspaces/${currentWorkspace.workspace_id}/invoice-settings`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ theme: invoiceTheme, logo: invoiceLogo, customColor, companyInfo, defaultTaxRate })
      });
      localStorage.setItem(`invoice_settings_${currentWorkspace.workspace_id}`, JSON.stringify({ theme: invoiceTheme, logo: invoiceLogo, customColor, companyInfo, defaultTaxRate }));
    } catch {}
  }, [currentWorkspace, invoiceTheme, invoiceLogo, customColor, companyInfo, defaultTaxRate]);
  
  useEffect(() => { if (settingsLoaded) saveInvoiceSettings(); }, [saveInvoiceSettings, settingsLoaded]);
  
  const fetchInvoices = useCallback(async () => {
    if (!currentWorkspace) return;
    setLoading(true);
    try {
      let url = `${API_URL}/api/invoices?workspace_id=${currentWorkspace.workspace_id}`;
      if (statusFilter !== 'all') url += `&status=${statusFilter}`;
      
      const response = await apiFetch(url);
      if (response.ok) {
        const raw = await response.json();
        // Guard: backend SHOULD always return an array. If a proxy / WAF
        // ever swaps in HTML or an error object, the downstream `.map` /
        // `.filter` calls below would crash and blank the page. Coerce
        // here so the page renders an empty state instead.
        const data = Array.isArray(raw) ? raw : [];
        setInvoices(data);
        
        const today = new Date();
        const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
        const outstanding = data.filter(i => ['draft', 'sent', 'partial', 'overdue'].includes(i.status));
        const overdue = data.filter(i => i.status === 'overdue');
        const paidThisMonth = data.filter(i => i.status === 'paid' && new Date(i.paid_date || i.updated_at) >= monthStart);
        
        const paidInvoices = data.filter(i => i.status === 'paid' && i.paid_date);
        let avgDays = 0;
        if (paidInvoices.length > 0) {
          const totalDays = paidInvoices.reduce((sum, inv) => {
            return sum + Math.floor((new Date(inv.paid_date) - new Date(inv.invoice_date)) / (1000 * 60 * 60 * 24));
          }, 0);
          avgDays = Math.round(totalDays / paidInvoices.length);
        }
        
        setArMetrics({
          total: outstanding.reduce((sum, i) => sum + (i.balance_due || 0), 0),
          overdue: overdue.reduce((sum, i) => sum + (i.balance_due || 0), 0),
          overdueCount: overdue.length,
          collected: paidThisMonth.reduce((sum, i) => sum + (i.total || 0), 0),
          avgDays,
          outstandingCount: outstanding.length,
        });
        
        let agingForInsight = { current: 0, days_1_30: 0, days_31_60: 0, days_61_90: 0, days_90_plus: 0 };
        try {
          const agingResponse = await apiFetch(`${API_URL}/api/invoices/aging-summary?workspace_id=${currentWorkspace.workspace_id}&include_drafts=true`);
          if (agingResponse.ok) {
            const agingData = await agingResponse.json();
            const b = agingData?.buckets || {};
            agingForInsight = {
              current: b.current || 0, days_1_30: b.days_1_30 || 0,
              days_31_60: b.days_31_60 || 0, days_61_90: b.days_61_90 || 0,
              days_90_plus: b.days_90_plus || 0,
            };
            setArAging(agingForInsight);
          }
        } catch {
          const todayDate = new Date(); todayDate.setHours(0, 0, 0, 0);
          outstanding.forEach(inv => {
            const dueDate = new Date(inv.due_date + 'T00:00:00');
            const daysOverdue = Math.floor((todayDate - dueDate) / (1000 * 60 * 60 * 24));
            const amount = inv.balance_due || 0;
            if (daysOverdue <= 0) agingForInsight.current += amount;
            else if (daysOverdue <= 30) agingForInsight.days_1_30 += amount;
            else if (daysOverdue <= 60) agingForInsight.days_31_60 += amount;
            else if (daysOverdue <= 90) agingForInsight.days_61_90 += amount;
            else agingForInsight.days_90_plus += amount;
          });
          setArAging(agingForInsight);
        }
        
        if (overdue.length > 0) {
          const totalOverdue = overdue.reduce((sum, i) => sum + (i.balance_due || 0), 0);
          setArInsight({ message: `${overdue.length} overdue invoice${overdue.length > 1 ? 's' : ''} totaling ${formatCurrency(totalOverdue)}. Consider sending payment reminders.`, type: 'warning' });
        } else if (agingForInsight.days_61_90 + agingForInsight.days_90_plus > 0) {
          setArInsight({ message: 'Some invoices aging past 60 days. Review collection strategies.', type: 'info' });
        } else { setArInsight(null); }
      }
    } catch { console.error('Failed to fetch invoices'); }
    finally { setLoading(false); }
  }, [currentWorkspace, statusFilter]);
  
  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

  // ─── Recurring Invoices ───
  const fetchRecurringInvoices = useCallback(async () => {
    if (!currentWorkspace) return;
    setRecurringLoading(true);
    try {
      const [recRes, custRes] = await Promise.all([
        apiFetch(`${API_URL}/api/workspaces/${currentWorkspace.workspace_id}/recurring-invoices?include_inactive=true`),
        apiFetch(`${API_URL}/api/contacts?workspace_id=${currentWorkspace.workspace_id}&type=customer`),
      ]);
      if (recRes.ok) {
        const recData = await recRes.json();
        setRecurringInvoices(Array.isArray(recData) ? recData : []);
      }
      if (custRes.ok) {
        const custData = await custRes.json();
        setCustomers(Array.isArray(custData) ? custData : []);
      }
    } catch (e) { console.error(e); }
    finally { setRecurringLoading(false); }
  }, [currentWorkspace]);

  useEffect(() => { if (viewTab === 'recurring') fetchRecurringInvoices(); }, [viewTab, fetchRecurringInvoices]);

  const handleSaveRecurring = async () => {
    if (!recForm.name.trim() || !recForm.customer_id) { toast.error('Name and customer required'); return; }
    if (!recForm.line_items.length) { toast.error('At least one line item required'); return; }
    setRecurringSaving(true);
    try {
      const res = await apiFetch(`${API_URL}/api/workspaces/${currentWorkspace.workspace_id}/recurring-invoices`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(recForm),
      });
      if (res.ok) { toast.success('Recurring invoice created'); setRecurringFormOpen(false); fetchRecurringInvoices(); }
      else { const e = await res.json(); toast.error(e.detail || 'Failed'); }
    } catch (e) { toast.error('Failed'); }
    finally { setRecurringSaving(false); }
  };

  const handleGenerateRecurring = async (recId) => {
    setGeneratingRec(recId);
    try {
      const res = await apiFetch(`${API_URL}/api/workspaces/${currentWorkspace.workspace_id}/recurring-invoices/${recId}/generate`, { method: 'POST' });
      if (res.ok) { const data = await res.json(); toast.success(data.message); fetchRecurringInvoices(); fetchInvoices(); }
      else { const e = await res.json(); toast.error(e.detail || 'Failed'); }
    } catch (e) { toast.error('Failed'); }
    finally { setGeneratingRec(null); }
  };

  const toggleRecurringActive = async (recId, active) => {
    await apiFetch(`${API_URL}/api/workspaces/${currentWorkspace.workspace_id}/recurring-invoices/${recId}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ active: !active }),
    });
    fetchRecurringInvoices();
  };

  const deleteRecurring = async (recId) => {
    await apiFetch(`${API_URL}/api/workspaces/${currentWorkspace.workspace_id}/recurring-invoices/${recId}`, { method: 'DELETE' });
    fetchRecurringInvoices(); toast.success('Deleted');
  };

  const addRecLine = () => setRecForm({ ...recForm, line_items: [...recForm.line_items, { description: '', quantity: 1, unit_price: 0, account_id: '', tax_rate: 0 }] });
  const removeRecLine = (i) => setRecForm({ ...recForm, line_items: recForm.line_items.filter((_, idx) => idx !== i) });
  const updateRecLine = (i, field, val) => { const li = [...recForm.line_items]; li[i] = { ...li[i], [field]: val }; setRecForm({ ...recForm, line_items: li }); };
  const FREQ_LABELS = { weekly: 'Weekly', biweekly: 'Bi-Weekly', monthly: 'Monthly', quarterly: 'Quarterly', yearly: 'Yearly' };

  // ─── Invoice Sort ───
  const [sortField, setSortField] = useState('invoice_no');
  const [sortDir, setSortDir] = useState('desc');
  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('desc'); }
  };
  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="w-3 h-3 text-muted-foreground/40" />;
    return sortDir === 'asc' ? <ArrowUp className="w-3 h-3 text-primary" /> : <ArrowDown className="w-3 h-3 text-primary" />;
  };
  
  const handlePost = async (invoiceId) => {
    setPosting(invoiceId);
    try {
      const response = await apiFetch(`${API_URL}/api/invoices/${invoiceId}/post`, { method: 'POST' });
      if (response.ok) {
        const data = await response.json().catch(() => ({}));
        const em = data.email || {};
        if (em.sent) {
          toast.success(`Invoice posted & emailed to ${em.to}`);
        } else if (em.reason === 'customer_no_email') {
          toast.warning('Invoice posted — but no email on file for this customer.', {
            description: 'Add an email address to the customer to enable delivery.'
          });
        } else if (em.reason) {
          toast.warning(`Invoice posted — email failed (${em.reason}).`, {
            description: em.hint || 'Check Resend logs or the customer email address.'
          });
        } else {
          toast.success('Invoice posted');
        }
        fetchInvoices();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to post');
      }
    } catch { toast.error('Failed to post invoice'); }
    finally { setPosting(null); }
  };
  
  const [deleting, setDeleting] = useState(null);
  const [resending, setResending] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(null);

  const handleDownloadInvoicePdf = async (invoiceId, invoiceNo) => {
    // One-click row PDF: fetch the server-side branded render, then route
    // through the shared downloadPdfBlob helper which uses the
    // /api/pdf/{id}?action=download wrapper for a real OS save dialog.
    setDownloadingPdf(invoiceId);
    try {
      const resp = await apiFetch(`${API_URL}/api/invoices/${invoiceId}/pdf`);
      if (!resp.ok) {
        const e = await resp.json().catch(() => ({}));
        throw new Error(e.detail || 'Could not fetch PDF');
      }
      const blob = await resp.blob();
      await downloadPdfBlob(blob, `Invoice_${invoiceNo || invoiceId}.pdf`, { mode: 'download' });
    } catch (e) {
      toast.error(e?.message || 'Could not download invoice');
    } finally {
      setDownloadingPdf(null);
    }
  };

  const handleResend = async (invoiceId) => {
    setResending(invoiceId);
    try {
      const response = await apiFetch(`${API_URL}/api/invoices/${invoiceId}/resend`, { method: 'POST' });
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        const em = data.email || {};
        if (em.sent) {
          toast.success(`Invoice emailed to ${em.to}`);
        } else if (em.reason === 'customer_no_email') {
          toast.warning('No email on file for this customer.', {
            description: em.hint || 'Add an email address to the customer to enable delivery.',
          });
        } else {
          toast.warning(`Email failed (${em.reason || 'unknown'}).`, {
            description: em.hint || 'Check Resend logs or the customer email address.',
          });
        }
        fetchInvoices();
      } else {
        toast.error(data.detail || 'Failed to send email');
      }
    } catch { toast.error('Failed to send invoice email'); }
    finally { setResending(null); }
  };

  const [reconciling, setReconciling] = useState(null);
  const handleReconcileStripe = async (invoice) => {
    setReconciling(invoice.invoice_id);
    try {
      const response = await apiFetch(
        `${API_URL}/api/invoices/${invoice.invoice_id}/reconcile-stripe`,
        { method: 'POST' },
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) { toast.error(data.detail || 'Reconcile failed'); return; }
      if (data.reconciled_count > 0) {
        toast.success(data.message, { description: `Invoice now: ${data.invoice_status}` });
      } else {
        toast.info(data.message);
      }
      fetchInvoices();
    } catch { toast.error('Reconcile failed'); }
    finally { setReconciling(null); }
  };

  const handleDeleteInvoice = async (invoice) => {
    if (!window.confirm(`Delete Invoice #${invoice.invoice_no}? This cannot be undone.`)) return;
    setDeleting(invoice.invoice_id);
    try {
      const response = await apiFetch(`${API_URL}/api/invoices/${invoice.invoice_id}`, { method: 'DELETE' });
      if (response.ok) { toast.success('Invoice deleted'); fetchInvoices(); }
      else { const error = await response.json(); toast.error(error.detail || 'Failed to delete'); }
    } catch { toast.error('Failed to delete invoice'); }
    finally { setDeleting(null); }
  };
  
  // Filtered and sorted invoices
  const filteredInvoices = invoices.filter(inv => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (inv.invoice_no?.toString().includes(q)) ||
      (inv.customer_name?.toLowerCase().includes(q)) ||
      (inv.reference?.toLowerCase().includes(q));
  }).sort((a, b) => {
    let valA, valB;
    switch (sortField) {
      case 'invoice_no': valA = a.invoice_no || 0; valB = b.invoice_no || 0; break;
      case 'customer_name': valA = (a.customer_name || '').toLowerCase(); valB = (b.customer_name || '').toLowerCase(); break;
      case 'invoice_date': valA = a.invoice_date || ''; valB = b.invoice_date || ''; break;
      case 'due_date': valA = a.due_date || ''; valB = b.due_date || ''; break;
      case 'total': valA = a.total || 0; valB = b.total || 0; break;
      case 'status': valA = a.status || ''; valB = b.status || ''; break;
      default: valA = a.invoice_no || 0; valB = b.invoice_no || 0;
    }
    if (valA < valB) return sortDir === 'asc' ? -1 : 1;
    if (valA > valB) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });
  
  // Status counts for filter tabs
  const statusCounts = {
    all: invoices.length,
    draft: invoices.filter(i => i.status === 'draft').length,
    sent: invoices.filter(i => i.status === 'sent').length,
    partial: invoices.filter(i => i.status === 'partial').length,
    paid: invoices.filter(i => i.status === 'paid').length,
    overdue: invoices.filter(i => i.status === 'overdue').length,
  };

  const getDaysUntilDue = (dueDate) => {
    if (!dueDate) return null;
    const diff = Math.ceil((new Date(dueDate + 'T00:00:00') - new Date()) / (1000 * 60 * 60 * 24));
    return diff;
  };
  
  return (
    <div className="space-y-8" data-testid="invoices-page">
      {/* Payments setup nudge — only shows when Stripe Connect isn't fully
          active for this workspace. Most invoices = most urgent moment to
          set up payments, so this banner converts better here than on /dashboard. */}
      <PaymentsSetupBanner workspaceId={currentWorkspace?.workspace_id} />

      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-foreground" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="invoices-heading">
            Invoices
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Track and manage your accounts receivable
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* Tab Toggle */}
          <div className="flex border rounded-lg overflow-hidden mr-2">
            <button onClick={() => setViewTab('invoices')} className={cn("px-3 py-1.5 text-xs font-medium transition-colors", viewTab === 'invoices' ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground")} data-testid="tab-invoices">
              <FileText className="w-3.5 h-3.5 inline mr-1" />Invoices
            </button>
            <button onClick={() => setViewTab('recurring')} className={cn("px-3 py-1.5 text-xs font-medium transition-colors", viewTab === 'recurring' ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground")} data-testid="tab-recurring">
              <Repeat className="w-3.5 h-3.5 inline mr-1" />Recurring
            </button>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="text-muted-foreground" data-testid="upload-dropdown-btn">
                <Upload className="w-4 h-4 mr-2" />
                Import
                <ChevronDown className="w-3.5 h-3.5 ml-1.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => navigate('/invoice-ocr?type=invoice')} data-testid="upload-single-invoice-btn">
                <Upload className="w-4 h-4 mr-2" /> Scan Invoice (OCR)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setInvoiceScannerOpen(true)} data-testid="ai-scan-invoice-btn">
                <ScanLine className="w-4 h-4 mr-2" /> AI Scan Invoice
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/batch-import?type=invoices')} data-testid="batch-import-invoices-btn">
                <FileUp className="w-4 h-4 mr-2" /> Batch Import (CSV)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button onClick={() => navigate('/invoices/new')}
            className="bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm" data-testid="new-invoice-btn">
            <Plus className="w-4 h-4 mr-2" />
            New Invoice
          </Button>
          <HelpPanel {...invoicesHelp} />
        </div>
      </div>
      
      {/* ─── RECURRING INVOICES TAB ─── */}
      {viewTab === 'recurring' && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">Set up invoices that generate automatically on a schedule</p>
            <Button onClick={() => { setRecForm({ name: '', customer_id: '', frequency: 'monthly', start_date: '', end_date: '', auto_send: false, memo: '', payment_terms: 30, line_items: [{ description: '', quantity: 1, unit_price: 0, account_id: '', tax_rate: 0 }] }); setRecurringFormOpen(true); }} data-testid="new-recurring-invoice-btn">
              <Plus className="w-4 h-4 mr-1" /> New Recurring Invoice
            </Button>
          </div>

          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
              <Table className="min-w-[820px]">
                <TableHeader>
                  <TableRow className="bg-muted/30">
                    <TableHead className="whitespace-nowrap">Name</TableHead>
                    <TableHead className="whitespace-nowrap">Customer</TableHead>
                    <TableHead className="whitespace-nowrap">Frequency</TableHead>
                    <TableHead className="whitespace-nowrap">Next Date</TableHead>
                    <TableHead className="whitespace-nowrap">Mode</TableHead>
                    <TableHead className="text-right whitespace-nowrap">Amount</TableHead>
                    <TableHead className="text-center whitespace-nowrap">Created</TableHead>
                    <TableHead className="whitespace-nowrap">Status</TableHead>
                    <TableHead className="text-right whitespace-nowrap">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recurringInvoices.length > 0 ? recurringInvoices.map(rec => {
                    const isDue = rec.active && rec.next_date && rec.next_date <= new Date().toISOString().split('T')[0];
                    return (
                      <TableRow key={rec.recurring_id} className={cn(!rec.active && "opacity-50")} data-testid={`rec-inv-${rec.recurring_id}`}>
                        <TableCell><p className="font-medium text-sm">{rec.name}</p></TableCell>
                        <TableCell className="text-sm">{rec.customer_name || '-'}</TableCell>
                        <TableCell><Badge variant="outline" className="text-[10px]">{FREQ_LABELS[rec.frequency] || rec.frequency}</Badge></TableCell>
                        <TableCell><span className={cn("text-sm", isDue ? "text-amber-600 font-medium" : "")}>{rec.next_date}{isDue && ' (Due!)'}</span></TableCell>
                        <TableCell><Badge className={cn("text-[9px]", rec.auto_send ? "bg-emerald-100 text-emerald-700" : "bg-blue-100 text-blue-700")}>{rec.auto_send ? 'Auto-Post' : 'Draft'}</Badge></TableCell>
                        <TableCell className="text-right text-sm">{formatCurrency(rec.total || 0)}</TableCell>
                        <TableCell className="text-center text-sm">{rec.invoices_created || 0}</TableCell>
                        <TableCell><Badge className={cn("text-[9px]", rec.active ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500")}>{rec.active ? 'Active' : 'Paused'}</Badge></TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center gap-1 justify-end">
                            {rec.active && (
                              <Button size="sm" variant="outline" onClick={() => handleGenerateRecurring(rec.recurring_id)} disabled={generatingRec === rec.recurring_id} data-testid={`generate-rec-${rec.recurring_id}`}>
                                {generatingRec === rec.recurring_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3 mr-1" />}Generate
                              </Button>
                            )}
                            <Button size="sm" variant="ghost" onClick={() => toggleRecurringActive(rec.recurring_id, rec.active)}>{rec.active ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}</Button>
                            <Button size="sm" variant="ghost" className="text-destructive" onClick={() => deleteRecurring(rec.recurring_id)}><Trash2 className="w-3 h-3" /></Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  }) : (
                    <TableRow><TableCell colSpan={9} className="text-center py-8">
                      <Repeat className="w-10 h-10 mx-auto mb-2 text-muted-foreground/50" />
                      <p className="text-muted-foreground">No recurring invoices</p>
                      <p className="text-xs text-muted-foreground mt-1">Create one to automate your billing</p>
                    </TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
              </div>
            </CardContent>
          </Card>

          {/* Create Recurring Invoice Dialog */}
          <Dialog open={recurringFormOpen} onOpenChange={setRecurringFormOpen}>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>New Recurring Invoice</DialogTitle>
                <DialogDescription>Set up an invoice that generates automatically on a schedule</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Template Name *</Label>
                    <Input value={recForm.name} onChange={e => setRecForm({...recForm, name: e.target.value})} placeholder="e.g., Monthly Retainer" data-testid="rec-inv-name" />
                  </div>
                  <div className="space-y-2">
                    <Label>Customer *</Label>
                    <Select value={recForm.customer_id} onValueChange={v => setRecForm({...recForm, customer_id: v})}>
                      <SelectTrigger data-testid="rec-inv-customer"><SelectValue placeholder="Select customer..." /></SelectTrigger>
                      <SelectContent>{customers.map(c => <SelectItem key={c.contact_id} value={c.contact_id}>{c.name}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-2">
                    <Label>Frequency</Label>
                    <Select value={recForm.frequency} onValueChange={v => setRecForm({...recForm, frequency: v})}>
                      <SelectTrigger data-testid="rec-inv-frequency"><SelectValue /></SelectTrigger>
                      <SelectContent>{Object.entries(FREQ_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Start Date *</Label>
                    <Input type="date" value={recForm.start_date} onChange={e => setRecForm({...recForm, start_date: e.target.value})} data-testid="rec-inv-start" />
                  </div>
                  <div className="space-y-2">
                    <Label>End Date (optional)</Label>
                    <Input type="date" value={recForm.end_date} onChange={e => setRecForm({...recForm, end_date: e.target.value})} />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-2">
                    <Label>Payment Terms (days)</Label>
                    <Input type="number" value={recForm.payment_terms} onChange={e => setRecForm({...recForm, payment_terms: parseInt(e.target.value) || 30})} />
                  </div>
                  <div className="space-y-2 flex items-end gap-2 pb-1">
                    <input type="checkbox" checked={recForm.auto_send} onChange={e => setRecForm({...recForm, auto_send: e.target.checked})} className="rounded" data-testid="rec-inv-auto-send" />
                    <Label className="text-sm">Auto-post when generated</Label>
                  </div>
                  <div className="space-y-2">
                    <Label>Memo</Label>
                    <Input value={recForm.memo} onChange={e => setRecForm({...recForm, memo: e.target.value})} placeholder="Optional" />
                  </div>
                </div>

                {/* Line Items */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="font-semibold">Line Items</Label>
                    <Button size="sm" variant="outline" onClick={addRecLine}><Plus className="w-3 h-3 mr-1" />Add Line</Button>
                  </div>
                  {recForm.line_items.map((li, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-end">
                      <div className="col-span-4">
                        {i === 0 && <Label className="text-[10px]">Description</Label>}
                        <Input className="h-9 text-xs" value={li.description} onChange={e => updateRecLine(i, 'description', e.target.value)} placeholder="Service description" />
                      </div>
                      <div className="col-span-2">
                        {i === 0 && <Label className="text-[10px]">Qty</Label>}
                        <Input className="h-9 text-xs" type="number" min="0" step="1" value={li.quantity} onChange={e => updateRecLine(i, 'quantity', parseFloat(e.target.value) || 0)} />
                      </div>
                      <div className="col-span-2">
                        {i === 0 && <Label className="text-[10px]">Unit Price</Label>}
                        <Input className="h-9 text-xs" type="number" min="0" step="0.01" value={li.unit_price} onChange={e => updateRecLine(i, 'unit_price', parseFloat(e.target.value) || 0)} />
                      </div>
                      <div className="col-span-2">
                        {i === 0 && <Label className="text-[10px]">Tax %</Label>}
                        <Input className="h-9 text-xs" type="number" min="0" step="0.01" value={li.tax_rate} onChange={e => updateRecLine(i, 'tax_rate', parseFloat(e.target.value) || 0)} />
                      </div>
                      <div className="col-span-1 text-right text-xs pt-1">
                        {formatCurrency(li.quantity * li.unit_price)}
                      </div>
                      <div className="col-span-1">
                        {recForm.line_items.length > 1 && <Button size="icon" variant="ghost" className="h-9 w-9 text-destructive" onClick={() => removeRecLine(i)}><Trash2 className="w-3 h-3" /></Button>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2 border-t">
                <Button variant="outline" onClick={() => setRecurringFormOpen(false)}>Cancel</Button>
                <Button onClick={handleSaveRecurring} disabled={recurringSaving} data-testid="save-recurring-invoice-btn">
                  {recurringSaving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Repeat className="w-4 h-4 mr-1" />}
                  Create Recurring Invoice
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      )}

      {/* ─── INVOICES TAB ─── */}
      {viewTab === 'invoices' && <>
      {/* Metrics Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="ar-metrics">
        <MetricCard
          label="Outstanding"
          value={loading ? '...' : formatCurrency(arMetrics.total || 0)}
          icon={DollarSign}
          trendLabel={`${arMetrics.outstandingCount || 0} invoices`}
          tooltip="Total amount owed by customers on unpaid invoices (draft, sent, partial, overdue)"
        />
        <MetricCard
          label="Overdue"
          value={loading ? '...' : formatCurrency(arMetrics.overdue || 0)}
          icon={AlertTriangle}
          variant={arMetrics.overdue > 0 ? 'danger' : 'default'}
          trendLabel={arMetrics.overdueCount > 0 ? `${arMetrics.overdueCount} past due` : 'None'}
          tooltip="Invoices past their due date that haven't been fully paid. Follow up on these."
        />
        <MetricCard
          label="Collected (MTD)"
          value={loading ? '...' : formatCurrency(arMetrics.collected || 0)}
          icon={TrendingUp}
          variant="success"
          trendLabel="This month"
          tooltip="Total payments received this month across all invoices"
        />
        <MetricCard
          label="Avg. Days to Pay"
          value={loading ? '...' : `${arMetrics.avgDays || 0} days`}
          icon={Clock}
          trendLabel="Payment velocity"
          tooltip="Average number of days between invoice date and payment. Lower is better — indicates faster collections."
        />
      </div>
      
      {/* Aging Breakdown */}
      {!loading && arMetrics.total > 0 && (
        <Card className="border-border shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">
                Aging Breakdown
              </span>
              <span className="text-xs text-muted-foreground">
                Total: {formatCurrency(arMetrics.total || 0)}
              </span>
            </div>
            <AgingBar aging={arAging} total={arMetrics.total || 0} />
          </CardContent>
        </Card>
      )}
      
      {/* AI Insight */}
      {arInsight && (
        <div className={cn(
          "flex items-start gap-3 px-4 py-3 rounded-xl border text-sm",
          arInsight.type === 'warning'
            ? "bg-amber-50 border-amber-200 text-amber-800 dark:bg-amber-900/10 dark:border-amber-900/30 dark:text-amber-300"
            : "bg-blue-50 border-blue-200 text-blue-800 dark:bg-blue-900/10 dark:border-blue-900/30 dark:text-blue-300"
        )} data-testid="ar-insight">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <p>{arInsight.message}</p>
        </div>
      )}
      
      {/* Recent OCR Uploads */}
      <RecentOCRUploads documentType="invoice" />
      
      {/* Saved Views */}
      <SavedViews
        page="invoices"
        presets={[
          { name: 'Overdue', icon: 'alert', filters: { status: 'overdue' }, sort: { field: 'total', dir: 'desc' } },
          { name: 'Highest Value', icon: 'dollar', filters: { status: 'all' }, sort: { field: 'total', dir: 'desc' } },
          { name: 'Newest First', icon: 'clock', filters: { status: 'all' }, sort: { field: 'invoice_date', dir: 'desc' } },
          { name: 'Due Soon', icon: 'calendar', filters: { status: 'all' }, sort: { field: 'due_date', dir: 'asc' } },
        ]}
        onApply={({ filters, sort }) => {
          if (filters.status) setStatusFilter(filters.status);
          if (filters.search) setSearch(filters.search);
          if (sort.field) { setSortField(sort.field); setSortDir(sort.dir || 'desc'); }
        }}
        currentFilters={{ status: statusFilter, search }}
        currentSort={{ field: sortField, dir: sortDir }}
      />

      {/* Search & Filters */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search invoices..."
            className="pl-9 h-9 text-sm"
            data-testid="invoice-search"
          />
        </div>
        <div className="flex items-center gap-1 p-1 rounded-lg bg-muted/50" data-testid="status-filter-tabs">
          {['all', 'draft', 'sent', 'partial', 'paid', 'overdue'].map(status => (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              className={cn(
                "px-3 py-1.5 text-xs font-medium rounded-md transition-all capitalize",
                statusFilter === status
                  ? "bg-white dark:bg-muted text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              )}
              data-testid={`filter-${status}`}
            >
              {status}
              {statusCounts[status] > 0 && (
                <span className={cn(
                  "ml-1.5 text-[10px] font-semibold",
                  statusFilter === status ? "text-muted-foreground" : "text-muted-foreground"
                )}>
                  {statusCounts[status]}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>
      
      {/* Invoice Table */}
      <Card className="border-border shadow-sm overflow-hidden">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-4">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="flex items-center gap-4">
                  <Skeleton className="h-4 w-20" />
                  <Skeleton className="h-4 w-40" />
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-6 w-16 rounded-full" />
                  <Skeleton className="h-4 w-20 ml-auto" />
                </div>
              ))}
            </div>
          ) : filteredInvoices.length === 0 ? (
            <div className="text-center py-20 px-6" data-testid="invoices-empty-state">
              <div className="w-16 h-16 mx-auto mb-5 rounded-2xl bg-muted flex items-center justify-center">
                <FileText className="w-7 h-7 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
                {search ? 'No matching invoices' : 'No invoices yet'}
              </h3>
              <p className="text-sm text-muted-foreground mb-6 max-w-sm mx-auto">
                {search ? 'Try a different search term' : 'Create your first invoice to start tracking receivables and get paid faster.'}
              </p>
              {!search && (
                <Button onClick={() => navigate('/invoices/new')} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Your First Invoice
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto pr-16">
              <Table className="min-w-[900px]">
                <TableHeader>
                  <TableRow className="bg-muted hover:bg-muted/30">
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap pl-5 cursor-pointer select-none" onClick={() => handleSort('invoice_no')} data-testid="sort-invoice">
                      <span className="flex items-center gap-1">Invoice <SortIcon field="invoice_no" /></span>
                    </TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap cursor-pointer select-none" onClick={() => handleSort('customer_name')} data-testid="sort-customer">
                      <span className="flex items-center gap-1">Customer <SortIcon field="customer_name" /></span>
                    </TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap cursor-pointer select-none" onClick={() => handleSort('invoice_date')} data-testid="sort-date">
                      <span className="flex items-center gap-1">Date <SortIcon field="invoice_date" /></span>
                    </TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap cursor-pointer select-none" onClick={() => handleSort('due_date')} data-testid="sort-due">
                      <span className="flex items-center gap-1">Due <SortIcon field="due_date" /></span>
                    </TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap cursor-pointer select-none" onClick={() => handleSort('status')} data-testid="sort-status">
                      <span className="flex items-center gap-1">Status <SortIcon field="status" /></span>
                    </TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-muted-foreground text-right whitespace-nowrap cursor-pointer select-none min-w-[100px]" onClick={() => handleSort('total')} data-testid="sort-total">
                      <span className="flex items-center gap-1 justify-end">Total <SortIcon field="total" /></span>
                    </TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider text-muted-foreground text-right whitespace-nowrap min-w-[100px]">Balance</TableHead>
                    <TableHead className="w-14 pr-6"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredInvoices.map(inv => {
                    const daysUntilDue = getDaysUntilDue(inv.due_date);
                    return (
                      <TableRow 
                        key={inv.invoice_id} 
                        className="group cursor-pointer hover:bg-muted/30 transition-colors"
                        data-testid={`invoice-row-${inv.invoice_id}`}
                        onClick={() => {
                          // Mobile: open the bottom-sheet 2-tap action menu.
                          // Desktop: keep the existing preview behavior.
                          if (typeof window !== 'undefined'
                              && window.matchMedia
                              && window.matchMedia('(max-width: 1023px)').matches) {
                            setMobileActionsInv(inv);
                          } else {
                            handlePreviewInvoice(inv);
                          }
                        }}
                      >
                        <TableCell className="pl-5">
                          <span className="text-sm font-medium text-foreground" data-testid={`invoice-number-${inv.invoice_id}`}>
                            #{inv.invoice_no}
                          </span>
                          {inv.reference && (
                            <span className="block text-[11px] text-muted-foreground mt-0.5">Ref: {inv.reference}</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <span className="text-sm font-medium text-foreground">{inv.customer_name}</span>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-muted-foreground">
                            {new Date(inv.invoice_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div>
                            <span className="text-sm text-muted-foreground">
                              {new Date(inv.due_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </span>
                            {inv.status !== 'paid' && inv.status !== 'voided' && daysUntilDue !== null && (
                              <span className={cn(
                                "block text-[10px] font-medium mt-0.5",
                                daysUntilDue < 0 ? "text-rose-500" :
                                daysUntilDue <= 7 ? "text-amber-500" :
                                "text-muted-foreground"
                              )}>
                                {daysUntilDue < 0 ? `${Math.abs(daysUntilDue)}d overdue` :
                                 daysUntilDue === 0 ? 'Due today' :
                                 `${daysUntilDue}d left`}
                              </span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <StatusBadge status={inv.status} />
                        </TableCell>
                        <TableCell className="text-right whitespace-nowrap">
                          <span className="text-sm font-medium text-foreground">
                            {formatCurrency(inv.total)}
                          </span>
                        </TableCell>
                        <TableCell className="text-right whitespace-nowrap">
                          <span className={cn(
                            "text-sm font-semibold",
                            inv.balance_due > 0 ? 'text-amber-600 dark:text-amber-400' : 'text-emerald-600 dark:text-emerald-400'
                          )}>
                            {formatCurrency(inv.balance_due)}
                          </span>
                        </TableCell>
                        <TableCell className="pr-4" onClick={(e) => e.stopPropagation()}>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`invoice-actions-${inv.invoice_id}`}>
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-44">
                              <DropdownMenuItem onClick={() => handlePreviewInvoice(inv)} data-testid={`preview-invoice-${inv.invoice_id}`}>
                                <Eye className="w-4 h-4 mr-2" /> Preview
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => handleDownloadInvoicePdf(inv.invoice_id, inv.invoice_no)}
                                disabled={downloadingPdf === inv.invoice_id}
                                data-testid={`download-invoice-${inv.invoice_id}`}
                              >
                                {downloadingPdf === inv.invoice_id
                                  ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                  : <Download className="w-4 h-4 mr-2" />}
                                Download PDF
                              </DropdownMenuItem>
                              {['draft', 'sent'].includes(inv.status) && (
                                <DropdownMenuItem onClick={() => handleEditInvoice(inv)} data-testid={`edit-invoice-${inv.invoice_id}`}>
                                  <Pencil className="w-4 h-4 mr-2" /> Edit
                                </DropdownMenuItem>
                              )}
                              {inv.status === 'draft' && (
                                <DropdownMenuItem onClick={() => handlePost(inv.invoice_id)} disabled={posting === inv.invoice_id} data-testid={`post-invoice-${inv.invoice_id}`}>
                                  <Send className="w-4 h-4 mr-2" /> Post & Send
                                </DropdownMenuItem>
                              )}
                              {['sent', 'partial', 'overdue'].includes(inv.status) && (
                                <DropdownMenuItem
                                  onClick={() => handleResend(inv.invoice_id)}
                                  disabled={resending === inv.invoice_id}
                                  data-testid={`resend-invoice-${inv.invoice_id}`}
                                >
                                  <Send className="w-4 h-4 mr-2" />
                                  {inv.email_sent ? 'Resend email' : 'Send email'}
                                </DropdownMenuItem>
                              )}
                              {['sent', 'partial', 'overdue'].includes(inv.status) && (
                                <DropdownMenuItem
                                  onClick={() => handleReconcileStripe(inv)}
                                  disabled={reconciling === inv.invoice_id}
                                  data-testid={`reconcile-stripe-${inv.invoice_id}`}
                                >
                                  {reconciling === inv.invoice_id
                                    ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    : <RefreshCw className="w-4 h-4 mr-2" />}
                                  Reconcile with Stripe
                                </DropdownMenuItem>
                              )}
                              {inv.journal_entry_id && (
                                <DropdownMenuItem asChild>
                                  <Link to={`/journal-entries/${inv.journal_entry_id}`} data-testid={`view-je-${inv.invoice_id}`}>
                                    <ExternalLink className="w-4 h-4 mr-2" /> View Journal Entry
                                  </Link>
                                </DropdownMenuItem>
                              )}
                              {inv.status === 'draft' && (
                                <>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    onClick={() => handleDeleteInvoice(inv)}
                                    disabled={deleting === inv.invoice_id}
                                    className="text-rose-600 dark:text-rose-400 focus:text-rose-600 focus:bg-rose-50 dark:focus:bg-rose-900/20"
                                    data-testid={`delete-invoice-${inv.invoice_id}`}
                                  >
                                    <Trash2 className="w-4 h-4 mr-2" /> Delete
                                  </DropdownMenuItem>
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Summary Footer */}
      {!loading && filteredInvoices.length > 0 && (
        <div className="flex items-center justify-between text-xs text-muted-foreground px-1">
          <span>{filteredInvoices.length} invoice{filteredInvoices.length !== 1 ? 's' : ''}</span>
          <span>
            Outstanding: <span className="font-semibold text-muted-foreground">{formatCurrency(arMetrics.total || 0)}</span>
          </span>
        </div>
      )}
      
      {/* Invoice Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto p-0" aria-describedby="invoice-preview-description">
          <div className="sticky top-0 z-10 bg-card border-b px-6 py-4">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-lg" style={{ fontFamily: "'Outfit', sans-serif" }}>
                <Eye className="w-5 h-5 text-muted-foreground" />
                Invoice Preview
                {previewInvoice && (
                  <Badge variant="outline" className="ml-2 text-[11px] rounded-full">
                    #{previewInvoice.invoice_no}
                  </Badge>
                )}
              </DialogTitle>
              <p id="invoice-preview-description" className="sr-only">Preview and customize invoice appearance</p>
            </DialogHeader>
          </div>
          
          <div className="p-6">
            {previewInvoice && (
              <InvoicePreview
                invoice={previewInvoice}
                companyInfo={companyInfo}
                theme={invoiceTheme}
                customColor={customColor}
                logo={invoiceLogo}
                defaultTaxRate={defaultTaxRate}
                onThemeChange={setInvoiceTheme}
                onLogoChange={setInvoiceLogo}
                onCustomColorChange={setCustomColor}
                onDefaultTaxRateChange={setDefaultTaxRate}
                showCustomization={true}
              />
            )}
            
            {/* Company Info Editor */}
            <details className="mt-6 print:hidden" data-testid="company-info-editor">
              <summary className="text-xs font-semibold uppercase tracking-[0.15em] cursor-pointer text-muted-foreground hover:text-foreground transition-colors">
                Customize Company Information
              </summary>
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4 p-5 border rounded-xl bg-muted">
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Company Name</Label>
                  <Input value={companyInfo.name} onChange={(e) => setCompanyInfo(prev => ({ ...prev, name: e.target.value }))} placeholder="Your Company Name" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Tagline</Label>
                  <Input value={companyInfo.tagline} onChange={(e) => setCompanyInfo(prev => ({ ...prev, tagline: e.target.value }))} placeholder="Your company tagline" />
                </div>
                <div className="space-y-2 col-span-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Address</Label>
                  <Input value={companyInfo.address} onChange={(e) => setCompanyInfo(prev => ({ ...prev, address: e.target.value }))} placeholder="123 Main St, City, State 12345" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Email</Label>
                  <Input value={companyInfo.email} onChange={(e) => setCompanyInfo(prev => ({ ...prev, email: e.target.value }))} placeholder="billing@company.com" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Phone</Label>
                  <Input value={companyInfo.phone} onChange={(e) => setCompanyInfo(prev => ({ ...prev, phone: e.target.value }))} placeholder="(555) 123-4567" />
                </div>
                <div className="space-y-2 col-span-2">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Website</Label>
                  <Input value={companyInfo.website} onChange={(e) => setCompanyInfo(prev => ({ ...prev, website: e.target.value }))} placeholder="www.yourcompany.com" />
                </div>
              </div>
            </details>
          </div>
        </DialogContent>
      </Dialog>

      {/* AI Invoice Scanner Dialog */}
      <Dialog open={invoiceScannerOpen} onOpenChange={setInvoiceScannerOpen}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ScanLine className="w-5 h-5 text-emerald-600" />
              AI Scan Invoice
            </DialogTitle>
            <DialogDescription>
              Upload an invoice PDF or image. AI will extract vendor, line items, and totals for review.
            </DialogDescription>
          </DialogHeader>
          {currentWorkspace && (
            <DocumentScanner
              workspaceId={currentWorkspace.workspace_id}
              docType="invoice"
              onImportSuccess={() => {
                setInvoiceScannerOpen(false);
                fetchInvoices();
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Mobile quick-actions bottom sheet — 2-tap send */}
      {mobileActionsInv && (
        <MobileQuickActions
          open={!!mobileActionsInv}
          onClose={() => setMobileActionsInv(null)}
          title={`#${mobileActionsInv.invoice_no} · ${mobileActionsInv.customer_name}`}
          subtitle={`${(mobileActionsInv.status || '').toUpperCase()} · ${formatCurrency(mobileActionsInv.total)}`}
          actions={[
            {
              icon: Eye,
              label: 'Preview',
              variant: 'primary',
              onClick: () => handlePreviewInvoice(mobileActionsInv),
              testid: `quick-preview-${mobileActionsInv.invoice_id}`,
            },
            ...(['sent', 'partial', 'overdue', 'draft'].includes(mobileActionsInv.status) ? [{
              icon: Send,
              label: mobileActionsInv.status === 'draft' ? 'Post & send email' : (mobileActionsInv.email_sent ? 'Resend email' : 'Send email'),
              variant: 'primary',
              hint: mobileActionsInv.email_sent_at
                ? `Last sent ${new Date(mobileActionsInv.email_sent_at).toLocaleString()}`
                : undefined,
              disabled: posting === mobileActionsInv.invoice_id || resending === mobileActionsInv.invoice_id,
              onClick: () => {
                if (mobileActionsInv.status === 'draft') handlePost(mobileActionsInv.invoice_id);
                else handleResend(mobileActionsInv.invoice_id);
              },
              testid: `quick-send-${mobileActionsInv.invoice_id}`,
            }] : []),
            {
              icon: Download,
              label: 'Download PDF',
              disabled: downloadingPdf === mobileActionsInv.invoice_id,
              onClick: () => handleDownloadInvoicePdf(mobileActionsInv.invoice_id, mobileActionsInv.invoice_no),
              testid: `quick-download-${mobileActionsInv.invoice_id}`,
            },
            ...(['draft', 'sent'].includes(mobileActionsInv.status) ? [{
              icon: Pencil,
              label: 'Edit',
              onClick: () => handleEditInvoice(mobileActionsInv),
              testid: `quick-edit-${mobileActionsInv.invoice_id}`,
            }] : []),
            ...(mobileActionsInv.status === 'draft' ? [{
              icon: Trash2,
              label: 'Delete',
              variant: 'destructive',
              disabled: deleting === mobileActionsInv.invoice_id,
              onClick: () => handleDeleteInvoice(mobileActionsInv),
              testid: `quick-delete-${mobileActionsInv.invoice_id}`,
            }] : []),
          ]}
        />
      )}
      </>}
    </div>
  );
}
